/**
 * Accessibility utilities for improved screen reader support and keyboard navigation
 */

let idCounter = 0;
export const generateId = (prefix = 'id'): string => {
  idCounter += 1;
  return `${prefix}-${idCounter}`;
};

export const announceToScreenReader = (
  message: string,
  priority: 'polite' | 'assertive' = 'polite'
): void => {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.textContent = message;

  document.body.appendChild(announcement);

  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

// Focus management utilities
export const focusElement = (selector: string): boolean => {
  const element = document.querySelector(selector) as HTMLElement;
  if (element) {
    element.focus();
    return true;
  }
  return false;
};

export const trapFocus = (container: HTMLElement): (() => void) => {
  const focusableElements = container.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  ) as NodeListOf<HTMLElement>;

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  const handleTabKey = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        lastElement.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastElement) {
        firstElement.focus();
        e.preventDefault();
      }
    }
  };

  container.addEventListener('keydown', handleTabKey);

  return () => {
    container.removeEventListener('keydown', handleTabKey);
  };
};

export const handleArrowNavigation = (
  e: KeyboardEvent,
  items: HTMLElement[],
  currentIndex: number,
  onSelect: (index: number) => void
): void => {
  let newIndex = currentIndex;

  switch (e.key) {
    case 'ArrowDown':
      newIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
      e.preventDefault();
      break;
    case 'ArrowUp':
      newIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
      e.preventDefault();
      break;
    case 'Home':
      newIndex = 0;
      e.preventDefault();
      break;
    case 'End':
      newIndex = items.length - 1;
      e.preventDefault();
      break;
    case 'Enter':
    case ' ':
      onSelect(currentIndex);
      e.preventDefault();
      break;
    default:
      return;
  }

  if (newIndex !== currentIndex) {
    items[newIndex]?.focus();
    onSelect(newIndex);
  }
};

export const getScreenReaderText = (text: string): string => {
  return text
    .replace(/[^\w\s]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

export const prefersReducedMotion = (): boolean => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

export const prefersHighContrast = (): boolean => {
  return window.matchMedia('(prefers-contrast: high)').matches;
};

export const getColorSchemePreference = ():
  | 'light'
  | 'dark'
  | 'no-preference' => {
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  if (window.matchMedia('(prefers-color-scheme: light)').matches) {
    return 'light';
  }
  return 'no-preference';
};
